import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { afterInteractions } from 'src/core/utils/afterInteractions';
import { useRoute, type RouteProp } from '@react-navigation/native';
import Loader from 'src/presentation/components/atoms/Loader';
import Colors from 'src/core/utils/Colors';
import type Constants from 'src/core/utils/Constants';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { handleEventDeeplink } from 'src/presentation/helpers/EventDeeplinkHandler';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';

/**
 * Invisible router screen where `/event/:code` deeplinks (HTTPS and topev://) land.
 */
function EventDeeplinkScreen() {
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenEventDeeplink>>();
	const isLoggedin = useAuthenticationStore((state) => state.isLoggedin);
	const code = route.params?.code ?? '';

	useEffect(() => {
		// Koude start: wacht tot init() de sessie heeft hersteld (null -> bool).
		if (isLoggedin === null) return;
		let cancelled = false;
		let interaction: ReturnType<typeof afterInteractions> | null = null;
		// Load the locally-stored events first, so a cold-start deeplink does not prompt to add an
		// event the user already has. The tabs (Index) are already the base route beneath this
		// resolver (see LinkingConfiguration.getStateFromPath), so we only run the decision tree:
		// it replaces this screen with the event, or pops back to the tabs. Wait for interactions
		// to settle first so iOS does not swallow the resulting navigation or alert.
		useEventStore
			.getState()
			.init()
			.finally(() => {
				if (cancelled) return;

				// Waits for the transition on native, runs straight away on web -- see the helper for
				// why waiting on web would leave this resolver on its loader.
				interaction = afterInteractions(() => {
					if (!cancelled) handleEventDeeplink(code);
				});
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
			interaction?.cancel();
		};
	}, [isLoggedin, code]);

	return (
		<View style={styles.root}>
			<Loader bgColor={Colors.primary} />
		</View>
	);
}

export default EventDeeplinkScreen;

const styles = StyleSheet.create({
	root: {
		backgroundColor: Colors.primary,
		flex: 1,
	},
});
